import { useEffect, useRef, useState, type ReactNode } from 'react';
import { formatEuro, formatZahl } from '../../lib/format';
import { alsEingabe, istAusdruck, pruefeBetrag, zahlFuerApi } from '../../lib/rechner';
import { Field, Input } from '../ui/input';

export interface BetragsfeldProps {
  label: ReactNode;
  /** Decimal-Text mit Punkt, wie die APIs ihn liefern und nehmen (`"190000.5"`), oder `''` für leer. */
  value: string;
  /**
   * Bei jeder Eingabe. `dezimal` ist der Decimal-Text, `''` bei leerem Feld und
   * `null`, solange die Eingabe keine Zahl ergibt (angefangene oder unlesbare
   * Rechnung). `roh` ist, was im Feld steht.
   */
  onChange: (dezimal: string | null, roh: string) => void;
  /**
   * Zeile unter dem Feld aus dem aktuellen Wert, z. B. Netto und Steuer;
   * `null`, solange keine Zahl dasteht. Steht eine Rechnung im Feld, kommt das
   * Ergebnis davor.
   */
  hinweis?: (wert: number | null) => ReactNode;
  /** Fehler von außen, etwa „Betrag fehlt“ beim Speichern. Geht vor „Rechnung nicht lesbar“. */
  error?: ReactNode;
  /** `prozent` für Zinssätze: Ergebnis als `3,875 %` statt in Euro. */
  einheit?: 'euro' | 'prozent';
  /** Stellen, auf die `dezimal` gerundet wird: 2 für Beträge, 4 für Zinssätze. */
  nachkommastellen?: number;
  placeholder?: string;
  required?: boolean;
  className?: string;
}

const NICHT_LESBAR = 'Rechnung nicht lesbar';

/**
 * Betragsfeld mit kleinem Rechner: „3000*1,19“ darf dastehen, das Ergebnis
 * erscheint darunter und wandert beim Verlassen ins Feld. Komma und Punkt
 * gelten beide, Tausenderpunkte werden erkannt. Die App bekommt immer einen
 * Decimal-Text, nie eine Gleitkommazahl. Ziffernblock am Handy; das Vorzeichen
 * gehört in eine eigene Auswahl („Ausgabe/Einnahme“), die iOS-Zifferntastatur
 * hat kein Minus.
 */
export function Betragsfeld({
  label,
  value,
  onChange,
  hinweis,
  error,
  einheit = 'euro',
  nachkommastellen = 2,
  placeholder,
  required,
  className,
}: BetragsfeldProps) {
  const [roh, setRoh] = useState(() => alsEingabe(value));
  // „3000*“ ist beim Tippen kein Fehler, nach dem Verlassen schon.
  const [verlassen, setVerlassen] = useState(false);
  const feld = useRef<HTMLInputElement>(null);
  // Was zuletzt gemeldet wurde: Kommt `value` so zurück, ist es das Echo der
  // eigenen Eingabe und überschreibt nichts.
  const gemeldet = useRef(value);
  // Ein Wert von außen, der während der Eingabe kam (etwa ein Zurücksetzen
  // nach dem Speichern per Enter). Er gilt beim Verlassen, wenn seitdem nicht
  // mehr getippt wurde; sonst zeigte das Feld etwas anderes als die App hält.
  const aufgeschoben = useRef<string | null>(null);

  useEffect(() => {
    if (value === gemeldet.current) return;
    gemeldet.current = value;
    // Ein von außen gesetzter Wert überschreibt keine laufende Eingabe.
    if (feld.current && feld.current === document.activeElement) {
      aufgeschoben.current = value;
      return;
    }
    aufgeschoben.current = null;
    setRoh(alsEingabe(value));
    setVerlassen(false);
  }, [value]);

  const melden = (neu: string) => {
    aufgeschoben.current = null;
    setRoh(neu);
    const p = pruefeBetrag(neu);
    const dezimal = p.art === 'zahl' ? zahlFuerApi(p.wert, nachkommastellen) : p.art === 'leer' ? '' : null;
    gemeldet.current = dezimal ?? '';
    onChange(dezimal, neu);
  };

  const ausrechnen = () => {
    const p = pruefeBetrag(roh);
    if (p.art === 'zahl' && p.rechnung) melden(alsEingabe(zahlFuerApi(p.wert, nachkommastellen)));
  };

  const pruefung = pruefeBetrag(roh);
  const wert = pruefung.art === 'zahl' ? pruefung.wert : null;
  const fehler =
    error ?? (pruefung.art === 'ungueltig' || (pruefung.art === 'unfertig' && verlassen) ? NICHT_LESBAR : undefined);

  const ergebnis =
    pruefung.art === 'zahl' && pruefung.rechnung
      ? `= ${einheit === 'prozent' ? `${formatZahl(pruefung.wert, { stellen: nachkommastellen })} %` : formatEuro(pruefung.wert, { cent: true })}`
      : null;
  const zusatz = hinweis?.(wert);
  const zeile =
    ergebnis && zusatz ? (
      <>
        {ergebnis} · {zusatz}
      </>
    ) : (
      (ergebnis ?? zusatz)
    );

  return (
    <Field label={label} hint={zeile || undefined} error={fehler} required={required} className={className}>
      {(attr) => (
        <Input
          {...attr}
          ref={feld}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          placeholder={placeholder}
          value={roh}
          onChange={(e) => {
            setVerlassen(false);
            melden(e.target.value);
          }}
          onBlur={() => {
            if (aufgeschoben.current !== null) {
              setRoh(alsEingabe(aufgeschoben.current));
              aufgeschoben.current = null;
              setVerlassen(false);
              return;
            }
            ausrechnen();
            setVerlassen(true);
          }}
          onKeyDown={(e) => {
            // Enter schickte sonst das Formular ab, bevor das Ergebnis einer
            // Rechnung je zu sehen war.
            if (e.key === 'Enter' && istAusdruck(roh)) {
              e.preventDefault();
              ausrechnen();
              setVerlassen(true);
            }
          }}
        />
      )}
    </Field>
  );
}
